import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';

const FAQ = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: "General",
      questions: [
        {
          q: "What is Stainless Steel 316L?",
          a: "Stainless Steel 316L is a low-carbon austenitic chromium-nickel stainless steel with molybdenum. The 'L' denotes low carbon content, which enhances corrosion resistance and weld integrity. It's widely used in chemical processing, pharmaceutical equipment, marine applications, and food processing due to its excellent corrosion resistance."
        },
        {
          q: "How does the AI analysis work?",
          a: "Our system uses Claude Sonnet 4, one of the most advanced AI models, combined with real-time web search to analyze chemical compatibility. The AI searches authoritative databases (NACE, ASTM, MSDS sources) and provides evidence-based analysis with source citations. Each report includes confidence levels based on data quality and availability."
        },
        {
          q: "How accurate are the analyses?",
          a: "Our analyses are based on verified data from authoritative sources and include confidence ratings (High/Medium/Low). However, this service is for informational purposes only. Always verify compatibility with official Safety Data Sheets (SDS) and consult qualified chemical engineers for critical applications. The accuracy depends on available data - well-documented chemicals have high confidence ratings with multiple source citations."
        }
      ]
    },
    {
      category: "Using the Service",
      questions: [
        {
          q: "What do the color codes mean?",
          a: "• RED (Not Compatible): The chemical will cause corrosion or damage to SS 316L. Avoid contact.\n• YELLOW (Merit Caution): Compatible under specific conditions (temperature, concentration, duration). Review detailed analysis.\n• GREEN (Compatible): Safe to use with SS 316L under normal conditions. Always check concentration and temperature limits in the detailed report."
        },
        {
          q: "What are T-Codes?",
          a: "T-Codes (Transport Codes) are classification codes used in international hazardous material transport regulations (IMDG, ADR, USDOT). They indicate the type of tank or container required for safe transport. Our reports provide T-Codes based on the chemical's UN number and hazard classification to help with transport compliance."
        },
        {
          q: "Can I export or save reports?",
          a: "Yes! You can:\n• Export reports as PDF with the download button\n• Bookmark important chemicals for quick reference\n• Access your full search history\n• Copy analysis text or take screenshots"
        },
        {
          q: "What information is included in reports?",
          a: "Each analysis includes:\n• Compatibility status (Not Compatible/Compatible/Caution)\n• Confidence level with data sources\n• UN number and T-Code for transport\n• Nitrogen transport requirements\n• Gas-tight seal recommendations\n• Cleaning procedures\n• Detailed technical analysis (corrosion rates, temperature effects, concentration impacts)\n• Safety notes and limitations"
        }
      ]
    },
    {
      category: "Subscription & Billing",
      questions: [
        {
          q: "How does the free trial work?",
          a: "New users get a 10-day free trial with unlimited access to all features. No credit card required to start. You can analyze as many chemicals as you need during the trial. After 10 days, you'll need to subscribe (£19/month) to continue accessing the service."
        },
        {
          q: "What's included in the £19/month subscription?",
          a: "Your subscription includes:\n• Unlimited chemical analyses\n• AI-powered compatibility reports with Claude Sonnet 4\n• Save and bookmark unlimited chemicals\n• Full search history tracking\n• Detailed reports with source citations\n• Transport code information (IMDG, ADR, USDOT)\n• Web-sourced data verification\n• Mobile and desktop access"
        },
        {
          q: "Can I cancel anytime?",
          a: "Yes, you can cancel your subscription at any time. Your access will continue until the end of your current billing period. No refunds for partial months, but you keep access for the time you've paid for."
        },
        {
          q: "Do you offer discounts for multiple users?",
          a: "Enterprise and team plans are in development. For now, each user needs their own subscription. Contact info@corrosioncheck.com if you need access for multiple team members - we can discuss custom solutions."
        }
      ]
    },
    {
      category: "Safety & Professional Use",
      questions: [
        {
          q: "Can I rely on this for safety-critical decisions?",
          a: "NO. This service provides informational analysis only. It should NEVER be used as the sole basis for safety-critical decisions. Always:\n• Verify with official Safety Data Sheets (SDS)\n• Consult qualified chemical engineers\n• Follow industry safety standards\n• Conduct physical testing when required\n• Follow all regulatory requirements\nUse our service as a preliminary research tool, not as final authority."
        },
        {
          q: "When should I consult a professional?",
          a: "Always consult a qualified chemical engineer or safety professional when:\n• Designing chemical storage or processing systems\n• Handling hazardous or toxic materials\n• Operating at extreme temperatures or pressures\n• Dealing with mixed chemicals or reactions\n• Compliance with safety regulations is required\n• Any uncertainty about compatibility exists\nOur service helps with preliminary research, but professionals provide liability, testing, and regulatory expertise."
        },
        {
          q: "What are the limitations of AI analysis?",
          a: "AI analysis limitations:\n• Data availability: Some chemicals have limited published compatibility data\n• Context-specific: Results may not account for your specific conditions (exact temperature, concentration, exposure time)\n• Static data: Chemical compatibility can vary with formulation changes\n• No physical testing: AI can't replace actual material testing\n• Conservative approach: AI may err on the side of caution when data is uncertain\nAlways verify critical information with primary sources."
        }
      ]
    },
    {
      category: "Technical Support",
      questions: [
        {
          q: "What if I can't find my chemical?",
          a: "Try these tips:\n• Use the common chemical name (e.g., 'Hydrochloric Acid' not 'HCl')\n• Check spelling variations\n• Try the IUPAC name\n• Include concentration if relevant (e.g., '98% Sulfuric Acid')\nIf you still can't get results, the chemical may have limited compatibility data available. Contact us at info@corrosioncheck.com and we can help research it."
        },
        {
          q: "Can I suggest features or improvements?",
          a: "Absolutely! We're constantly improving the service. Send feature requests, bug reports, or general feedback to info@corrosioncheck.com. We prioritize features based on user demand."
        },
        {
          q: "How do I get support?",
          a: "Email us at info@corrosioncheck.com for:\n• Technical issues\n• Billing questions\n• Feature requests\n• General inquiries\nWe typically respond within 24 hours on business days."
        }
      ]
    }
  ];

  const toggleQuestion = (categoryIndex, questionIndex) => {
    const index = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <header className="glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate(-1)}
              variant="ghost"
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              FAQ & Help
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12 space-y-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>
            Frequently Asked Questions
          </h2>
          <p className="text-white/80">
            Everything you need to know about Chem-Steel 316L Compatibility Checker
          </p>
        </div>

        {faqs.map((category, catIndex) => (
          <div key={catIndex} className="space-y-4">
            <h3 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              {category.category}
            </h3>
            
            {category.questions.map((faq, qIndex) => {
              const isOpen = openIndex === `${catIndex}-${qIndex}`;
              return (
                <Card key={qIndex} className="glass border-white/20">
                  <CardContent className="p-0">
                    <button
                      onClick={() => toggleQuestion(catIndex, qIndex)}
                      className="w-full text-left p-6 flex items-center justify-between hover:bg-white/5 transition-colors"
                    >
                      <span className="text-white font-semibold pr-4">{faq.q}</span>
                      {isOpen ? (
                        <ChevronUp className="w-5 h-5 text-white flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-white flex-shrink-0" />
                      )}
                    </button>
                    
                    {isOpen && (
                      <div className="px-6 pb-6">
                        <p className="text-white/90 whitespace-pre-line leading-relaxed">
                          {faq.a}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ))}

        <Card className="glass border-white/20 mt-12">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-semibold text-white mb-4">Still have questions?</h3>
            <p className="text-white/80 mb-6">
              Can't find the answer you're looking for? Get in touch with our support team.
            </p>
            <a
              href="mailto:info@corrosioncheck.com"
              className="inline-block bg-white text-purple-700 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Contact Support
            </a>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default FAQ;
